import React from 'react';
import { useForm, FormProvider } from 'react-hook-form/dist/index.ie11';
import { Button, Column, Row, Stack } from '@grubhubprod/gh-cookbook-react';
import { EmailInput } from 'src/components/EmployeeForm/EmailInput/EmailInput';
import {
  Employee,
  EmployeeFormLabel,
  EmployeeValidationErrors,
  emptyEmployeeFormFields,
  getEmployeeValidation,
  useEmployeeFormAnalytics,
} from 'src/components/EmployeeForm/EmployeeForm.utils';
import { FirstNameInput } from 'src/components/EmployeeForm/FirstNameInput/FirstNameInput';
import { LastNameInput } from 'src/components/EmployeeForm/LastNameInput/LastNameInput';
import {
  API_ERROR_MESSAGES_KEYS,
  pushErrorFromAPI,
} from 'src/utilities/notifications';

export interface EmployeeFormProps {
  onAdd: (employee: Employee) => void;
  emailValidation: (value: string) => string | boolean;
  corpId: string;
}

export function EmployeeForm({
  onAdd,
  emailValidation,
  corpId,
}: EmployeeFormProps): JSX.Element {
  const useFormProps = useForm<Employee>({
    mode: 'all',
    defaultValues: emptyEmployeeFormFields,
  });
  const {
    sendAnalyticsOnSuccess,
    sendAnalyticsOnValidationErrors,
    sendAnalyticsOnEmailAlreadyExistsError,
  } = useEmployeeFormAnalytics();

  const { handleSubmit, formState, reset, setError } = useFormProps;
  const { isValid, isSubmitting } = formState;

  const setEmailAlreadyExistsError = () => {
    setError('email', {
      message:
        'Email already exists in the system, please assign them to a line of credit',
      type: 'validate',
    });
    sendAnalyticsOnEmailAlreadyExistsError();
  };

  const setValidationErrors = (
    validationErrors: EmployeeValidationErrors[] = []
  ) => {
    const analyticsErrorsList = [];
    validationErrors.forEach(({ message_key, property: field }) => {
      const message =
        API_ERROR_MESSAGES_KEYS[`${field}.${message_key}`] ||
        `${EmployeeFormLabel[field]} is not valid`;

      setError(field, {
        message,
        type: 'validate',
      });
      analyticsErrorsList.push(message);
    });
    sendAnalyticsOnValidationErrors(analyticsErrorsList.join(','));
  };

  async function onFormSubmit(employee: Employee) {
    const payload = {
      ...employee,
    };

    try {
      const {
        already_exists: emailAlreadyExists,
        validation_errors: validationErrors = [],
      } = await getEmployeeValidation(corpId, payload);

      if (validationErrors.length > 0) {
        setValidationErrors(validationErrors);
        return;
      }

      if (emailAlreadyExists) {
        setEmailAlreadyExistsError();
        return;
      }
    } catch (error) {
      pushErrorFromAPI(error);
      return;
    }
    onAdd(payload);
    reset(emptyEmployeeFormFields);
    sendAnalyticsOnSuccess();
  }

  return (
    <FormProvider {...useFormProps}>
      <form>
        <Stack y="sm">
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <FirstNameInput name="first_name" id="firstName" />
            </Column>
            <Column gutter={12} phone={6}>
              <LastNameInput name="last_name" id="lastName" />
            </Column>
          </Row>
        </Stack>
        <Stack y="sm">
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <EmailInput
                name="email"
                id="email"
                validation={emailValidation}
              />
            </Column>
          </Row>
        </Stack>

        <Stack x="xx-lg">
          <Row>
            <Column phone={4}>
              <Button
                block
                variant="primary"
                buttonSize="medium"
                disabled={!isValid || isSubmitting}
                isLoading={isSubmitting}
                onClick={handleSubmit(onFormSubmit)}
              >
                Add
              </Button>
            </Column>
          </Row>
        </Stack>
      </form>
    </FormProvider>
  );
}
